import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type {
  DriveBackupScope,
  EcwCardExportFormat,
  EcwCardExportTarget,
  ExportEcwCardsResult,
  ExportParticipantsResult,
  ExportPostsResult,
  ExportStreetSurveyResult,
  ParticipantExportFormat,
  PostExportFormat,
  StreetSurveyExportFormat,
  StreetSurveyExportRowMode,
} from '@citizen-walk/shared-types';
import { listStreetSurveyOptions, resolvePhotoPrivacySettings } from '@citizen-walk/shared-types';
import { zip } from 'fflate';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { defaultStreetSurveyLabel } from '../street-survey';
import { useDriveBackup } from './useDriveBackup';
import { useExportPhotos } from './useExportPhotos';
import { triggerParticipantsDownload, useExportParticipants } from './useExportParticipants';
import { triggerDownload, useExportPosts } from './useExportPosts';
import { triggerEcwCardsDownload, useExportEcwCards } from './useExportEcwCards';
import { triggerStreetSurveyDownload, useExportStreetSurvey } from './useExportStreetSurvey';
import { ExportPreviewPanel } from './ExportPreviewPanel';

/**
 * Export 画面 (admin 第 14 弾、Phase 5 REQ-4.3.14 / 4.3.18 / 8.1.3 / 9.2.1)。
 *
 * 旧 `old/apps/admin/src/pages/ExportPage.tsx` の UI を v2 callable + shared-ui に合わせて移植。
 *
 * 旧との差分:
 *   - CSV / GeoJSON 生成は server callable に統合 (個人情報 toggle / operationLogs 記録)
 *   - Drive バックアップは scope 外 (旧も skeleton 状態、別 callable で改めて実装予定)
 *   - 印刷 (card-print) は別 feature、ここから link のみ提示
 *   - 個人情報除外 toggle を明示 (REQ-4.3.18 研究者向け匿名化 dataset)
 *
 * 認可: callable 側 `requireAdmin(districtId)` で master / district admin (担当地区) のみ許可。
 */
export function ExportPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const { state: exportState, call, reset } = useExportPosts();
  // Sprint BB: Drive backup (Cloud Storage + signed URL) + photo export (signed URL list)。
  const {
    state: driveBackupState,
    call: callDriveBackup,
    reset: resetDriveBackup,
  } = useDriveBackup();
  const {
    state: photoExportState,
    call: callPhotoExport,
    reset: resetPhotoExport,
  } = useExportPhotos();
  const {
    state: participantsExportState,
    call: callParticipantsExport,
    reset: resetParticipantsExport,
  } = useExportParticipants();
  const { state: ecwCardsState, call: callEcwCards, reset: resetEcwCards } = useExportEcwCards();
  const {
    state: streetSurveyState,
    call: callStreetSurvey,
    reset: resetStreetSurvey,
  } = useExportStreetSurvey();
  const { toast } = useToast();
  const [includePersonal, setIncludePersonal] = useState(false);
  const [photoThumbnailOnly, setPhotoThumbnailOnly] = useState(false);
  // docs/28 §5-5: ぼかし前の写真 (元画像) で出力する。
  const [photoUnblurred, setPhotoUnblurred] = useState(false);
  // 2026-09-18: ダウンロード前に中身を確認できるようにする (押すと即 download だったため、
  // 列構成・ラベル・件数を開くまで確かめられなかった)。callable が返す実物をそのまま表示する。
  const [postsPreview, setPostsPreview] = useState<ExportPostsResult | null>(null);
  const [participantsPreview, setParticipantsPreview] = useState<ExportParticipantsResult | null>(
    null,
  );
  const [ecwPreview, setEcwPreview] = useState<ExportEcwCardsResult | null>(null);
  const [ecwTarget, setEcwTarget] = useState<EcwCardExportTarget>('insights');
  // 机上調査: 調査 (既定 / 追加) と行の単位の選択。
  const [surveyPreview, setSurveyPreview] = useState<ExportStreetSurveyResult | null>(null);
  const [surveyKey, setSurveyKey] = useState<string>('default');
  const [surveyRowMode, setSurveyRowMode] = useState<StreetSurveyExportRowMode>('points');
  const district = districtState.status === 'ready' ? districtState.district : null;
  const surveyOptions = useMemo(
    () => listStreetSurveyOptions(district?.aiAnalysis, defaultStreetSurveyLabel(district)),
    [district],
  );
  const streetSurveyEnabled = district?.streetView?.enabled === true;
  // Sprint BB Part 3.5 — client 側 ZIP bundling (fflate) の進捗 state。
  const [photoZipState, setPhotoZipState] = useState<
    | { status: 'idle' }
    | { status: 'pending'; done: number; total: number; failed: number }
    | { status: 'success'; total: number; failed: number }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canExport = isMaster || isDistrictAdminOfThis;
  // Sprint BB: Drive backup + 写真 ZIP は master 限定 (個人写真一括 download は高リスク、
  // district admin への開放は安全性レビュー後)。
  const canMasterOnly = isMaster;
  // docs/28 §5-5 (D8): ぼかし前の写真のエクスポートは master と、地区設定で許可された地区管理者だけ。
  const canExportUnblurred =
    isMaster ||
    (isDistrictAdminOfThis &&
      districtState.status === 'ready' &&
      resolvePhotoPrivacySettings(districtState.district.photoPrivacy)
        .allowDistrictAdminOriginalExport);

  async function handleExport(format: PostExportFormat) {
    if (!districtId || !canExport) return;
    if (includePersonal) {
      const ok = confirm(
        '個人情報 (authorUid / authorNickname / sessionId / 写真パス) を含めて出力します。' +
          '取扱には十分注意してください。続行しますか？',
      );
      if (!ok) return;
    }
    const result = await call({ districtId, format, includePersonal });
    if (result) {
      triggerDownload(result);
      toast(
        'success',
        `${format.toUpperCase()} を出力しました (${result.rowCount} 件 / 全 ${result.totalCount} 件中)`,
      );
    }
  }

  /** 投稿のプレビュー (ダウンロードせず、実際の出力の先頭だけを表示する)。 */
  async function handlePostsPreview(format: PostExportFormat) {
    if (!districtId || !canExport) return;
    const result = await call({ districtId, format, includePersonal, preview: true });
    if (result) setPostsPreview(result);
  }

  /** 参加者のプレビュー。 */
  async function handleParticipantsPreview(format: ParticipantExportFormat) {
    if (!districtId || !canExport) return;
    const result = await callParticipantsExport({ districtId, format, preview: true });
    if (result) setParticipantsPreview(result);
  }

  /** 気づき / 処方箋 のプレビューとダウンロード。 */
  async function handleEcwCards(format: EcwCardExportFormat, preview: boolean) {
    if (!districtId || !canExport) return;
    if (!preview && includePersonal) {
      const ok = confirm(
        '作成者のユーザーID・ニックネーム' +
          (ecwTarget === 'ideas' ? '・本名' : '') +
          'を含めて出力します。取扱には十分注意してください。続行しますか？',
      );
      if (!ok) return;
    }
    const result = await callEcwCards({
      districtId,
      target: ecwTarget,
      format,
      includePersonal,
      preview,
    });
    if (!result) return;
    if (preview) {
      setEcwPreview(result);
      return;
    }
    triggerEcwCardsDownload(result);
    toast(
      'success',
      `${ecwTarget === 'insights' ? '気づき' : '処方箋'}を ${format.toUpperCase()} で出力しました (${result.rowCount} 件)`,
    );
  }

  /** 机上調査の結果の出力 / プレビュー。 */
  async function handleStreetSurvey(format: StreetSurveyExportFormat, preview: boolean) {
    if (!districtId || !canExport) return;
    const result = await callStreetSurvey({
      districtId,
      format,
      surveyKey,
      rowMode: surveyRowMode,
      preview,
    });
    if (!result) return;
    if (preview) {
      setSurveyPreview(result);
      return;
    }
    triggerStreetSurveyDownload(result);
    toast(
      'success',
      `机上調査を ${format.toUpperCase()} で出力しました (地点 ${result.pointCount} / 検出 ${result.detectionCount})`,
    );
  }

  /**
   * 参加者 (一般ユーザー) 登録情報の CSV / JSON 出力。
   * 投稿アプリに表示されない登録メタ (同意日時・登録日時・地区独自質問への回答) も含むため、
   * 個人情報の取扱い注意を confirm で明示してから callable を叩く。
   */
  async function handleParticipantsExport(format: ParticipantExportFormat) {
    if (!districtId || !canExport) return;
    const ok = confirm(
      '参加者の登録情報 (ニックネーム / 属性 / 同意日時 / 地区独自の質問への回答など) を' +
        `${format.toUpperCase()} で出力します。個人情報を含むため取扱いには十分注意してください。続行しますか？`,
    );
    if (!ok) return;
    const result = await callParticipantsExport({ districtId, format });
    if (result) {
      triggerParticipantsDownload(result);
      toast(
        'success',
        `参加者一覧を ${format.toUpperCase()} で出力しました (${result.rowCount} 名)`,
      );
    }
  }

  /**
   * Sprint BB Part 2 — Drive backup (Cloud Storage に CSV + GeoJSON 書込 + signed URL 取得)。
   * Drive 直接 upload は次 sprint で追加予定 (現状 driveFolderUrl=null)。
   */
  async function handleDriveBackup(scope: DriveBackupScope) {
    if (!districtId || !canMasterOnly) return;
    const ok = confirm(
      `${scope === 'all' ? '全データ' : '投稿'} を Cloud Storage にバックアップします。\n` +
        `${includePersonal ? '個人情報あり' : '匿名化'} で出力、signed URL は 5 分有効です。\n続行しますか？`,
    );
    if (!ok) return;
    const result = await callDriveBackup({ districtId, scope, includePersonal });
    if (result) {
      toast(
        'success',
        `バックアップ完了: ${result.files.length} file (${result.rowCount} 件 / 全 ${result.totalCount} 件中)`,
      );
    }
  }

  /**
   * Sprint BB Part 3 — 写真一括 download (signed URL list 返却)。
   * Sprint BB Part 3.5 で fflate (~10KB, zero-dep) による client 側 ZIP bundling を追加。
   */
  async function handlePhotoExport() {
    if (!districtId || !canExport) return;
    const ok = confirm(
      `写真を一括 download します (${photoThumbnailOnly ? 'thumbnail のみ' : 'original + thumbnail'})。\n` +
        `signed URL は 15 分有効、新タブで個別 download します。続行しますか？`,
    );
    if (!ok) return;
    const result = await callPhotoExport({
      districtId,
      thumbnailOnly: photoThumbnailOnly,
      unblurredOriginals: canExportUnblurred && photoUnblurred && !photoThumbnailOnly,
    });
    if (result) {
      setPhotoZipState({ status: 'idle' });
      toast(
        'success',
        `写真 URL を取得しました: ${result.files.length} file / ${result.photoCount} post (全 ${result.postCount} 件中)`,
      );
    }
  }

  /**
   * Sprint BB Part 3.5 — fflate による client 側 ZIP bundling。
   * 並列 fetch (concurrency=4) → Uint8Array に集約 → fflate.zip (async) → Blob download。
   * 個別 fetch 失敗は skip して継続、最後に failed 件数を summary 表示。
   */
  async function handlePhotoZipDownload() {
    if (photoExportState.status !== 'success') return;
    const files = photoExportState.result.files;
    if (files.length === 0) return;
    if (!districtId) return;

    const total = files.length;
    setPhotoZipState({ status: 'pending', done: 0, total, failed: 0 });

    const entries: Record<string, Uint8Array> = {};
    let done = 0;
    let failed = 0;
    const CONCURRENCY = 4;

    // 重複 filename を避けるため必要なら suffix を付ける。
    function uniqueName(name: string): string {
      if (!(name in entries)) return name;
      const dot = name.lastIndexOf('.');
      const base = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : '';
      let i = 1;
      while (`${base}_${i}${ext}` in entries) i += 1;
      return `${base}_${i}${ext}`;
    }

    async function fetchOne(idx: number) {
      const f = files[idx];
      if (!f) return;
      try {
        const res = await fetch(f.downloadUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = new Uint8Array(await res.arrayBuffer());
        entries[uniqueName(f.filename)] = buf;
      } catch (e) {
        failed += 1;
        console.warn('[photo-zip] fetch failed', f.filename, e);
      } finally {
        done += 1;
        if (done % Math.max(1, Math.floor(total / 10)) === 0 || done === total) {
          toast('info', `写真ダウンロード中: ${done}/${total}`);
        }
        setPhotoZipState({ status: 'pending', done, total, failed });
      }
    }

    // worker pool。
    let cursor = 0;
    async function worker() {
      while (cursor < total) {
        const idx = cursor;
        cursor += 1;
        await fetchOne(idx);
      }
    }
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, () => worker()));

      if (Object.keys(entries).length === 0) {
        setPhotoZipState({ status: 'error', message: '全ての写真の取得に失敗しました。' });
        toast('error', '全ての写真の取得に失敗しました。');
        return;
      }

      // 投稿データとの紐付け維持 (ユーザー要望): 写真ファイル名 (= {postId}_*.jpg) と投稿を対応づける
      // manifest を ZIP に同梱する。
      //   - manifest.csv: filename↔postId↔kind の対応表 (常に同梱)
      //   - posts.csv: 投稿データ一覧 (CSV callable から取得して同梱、失敗時は skip)
      //   - README.txt: 紐付け方法の説明
      const enc = new TextEncoder();
      const manifestRows = ['filename,postId,kind'];
      for (const f of files) {
        manifestRows.push(`${f.filename},${f.postId},${f.kind}`);
      }
      entries['manifest.csv'] = enc.encode('﻿' + manifestRows.join('\r\n') + '\r\n');
      try {
        const csvResult = await call({ districtId, format: 'csv', includePersonal });
        if (csvResult?.content) {
          entries['posts.csv'] = enc.encode(csvResult.content);
        }
      } catch {
        // posts.csv 取得失敗は致命ではない (manifest.csv で最低限の紐付けは保たれる)。
        console.warn('[photo-zip] posts.csv の同梱に失敗しました (manifest.csv のみ同梱)');
      }
      entries['README.txt'] = enc.encode(
        [
          'CitizenWalk 投稿写真エクスポート',
          '',
          '写真ファイル名は "{postId}_original.jpg" / "{postId}_thumb.jpg" 形式です。',
          'manifest.csv : 各写真ファイルと postId・種別 (original/thumbnail) の対応表。',
          'posts.csv    : 投稿データ一覧。postId 列で写真ファイルと紐付けできます (同梱されない場合あり)。',
          '',
          `district : ${districtId}`,
          `exported : ${new Date().toISOString()}`,
        ].join('\r\n'),
      );

      const zipped = await new Promise<Uint8Array>((resolve, reject) => {
        zip(entries, { level: 6 }, (err, data) => {
          if (err) reject(err);
          else resolve(data);
        });
      });

      const today = new Date().toISOString().slice(0, 10);
      // ArrayBuffer に cast (fflate は Uint8Array<ArrayBufferLike> を返すが Blob は ArrayBuffer 要求)。
      const blob = new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photos_${districtId}_${today}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setPhotoZipState({ status: 'success', total, failed });
      toast(
        'success',
        failed > 0
          ? `ZIP 作成完了: ${total - failed}/${total} (失敗 ${failed})`
          : `ZIP 作成完了: ${total} file`,
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setPhotoZipState({ status: 'error', message });
      toast('error', `ZIP 作成失敗: ${message}`);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          エクスポート
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.3.14 / 4.3.18 — 投稿の CSV / GeoJSON 出力 (個人情報あり / 匿名化を選択)。
        </p>
      </header>

      {!canExport && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のエクスポート権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">出力オプション</h2>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includePersonal}
            onChange={(e) => setIncludePersonal(e.target.checked)}
            disabled={!canExport || exportState.status === 'pending'}
            className="mt-1"
          />
          <span className="text-sm text-slate-700">
            <strong>個人情報を含める</strong> (authorUid / authorNickname / sessionId / 写真パス /
            moderation 履歴)
            <br />
            <span className="text-xs text-slate-500">
              チェックを外すと研究者向け匿名化 dataset として出力します (REQ-4.3.18 / 8.1.3)。
            </span>
          </span>
        </label>
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">ダウンロード</h2>
        <p className="text-xs text-slate-500 mb-3">
          地区配下の投稿 (公開 / 本人のみ) を一括出力します。各投稿の内容に加えて
          <strong>AI 解析結果（投稿概要・説明・スコア・分類など）</strong>も含まれます。CSV
          は読みやすい日本語の列に展開し、GeoJSON
          は解析結果の構造をそのまま埋め込みます。アーカイブ済
          と全員非表示の投稿は除外されます。出力操作は <code>operationLogs</code> に記録されます。
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => void handleExport('csv')}
            disabled={!canExport || exportState.status === 'pending'}
          >
            {exportState.status === 'pending' ? '生成中…' : '📄 CSV をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleExport('geojson')}
            disabled={!canExport || exportState.status === 'pending'}
          >
            {exportState.status === 'pending' ? '生成中…' : '🗺 GeoJSON をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handlePostsPreview('csv')}
            disabled={!canExport || exportState.status === 'pending'}
          >
            👁 CSV の中身を確認
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handlePostsPreview('geojson')}
            disabled={!canExport || exportState.status === 'pending'}
          >
            👁 GeoJSON の中身を確認
          </Button>
        </div>
        {postsPreview && (
          <ExportPreviewPanel
            content={postsPreview.content}
            format={postsPreview.format === 'csv' ? 'csv' : 'geojson'}
            totalRowCount={postsPreview.rowCount}
            title={`投稿 ${postsPreview.format.toUpperCase()} のプレビュー`}
            onDownload={() => void handleExport(postsPreview.format)}
            onClose={() => setPostsPreview(null)}
          />
        )}
      </Card>

      {exportState.status === 'error' && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">エクスポート失敗: {exportState.error.message}</p>
          <button
            type="button"
            onClick={reset}
            className="mt-2 text-xs text-primary-600 hover:underline"
          >
            エラーを閉じる
          </button>
        </Card>
      )}

      {/* 参加者 (一般ユーザー) 登録情報の CSV / JSON 出力。投稿アプリに表示されない登録メタも含む。
          master / 担当地区管理者が実行可、PII を含むため operationLogs に記録される。 */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">👥 参加者一覧（登録情報）</h2>
        <p className="text-xs text-slate-500 mb-3">
          この地区に登録した参加者の登録情報を一括出力します。ニックネーム・属性 (性別 / 年代 /
          関係など) のほか、投稿アプリには表示されない
          <strong>同意日時・登録日時・地区独自の質問への回答</strong>
          も含みます。引き継ぎコードなどの機密値は出力されません。CSV
          の列見出しと選択肢はこの地区の入力項目設定に合わせた日本語ラベルになります。出力操作は{' '}
          <code>operationLogs</code> に記録されます。
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => void handleParticipantsExport('csv')}
            disabled={!canExport || participantsExportState.status === 'pending'}
          >
            {participantsExportState.status === 'pending' ? '生成中…' : '📄 CSV をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleParticipantsExport('json')}
            disabled={!canExport || participantsExportState.status === 'pending'}
          >
            {participantsExportState.status === 'pending' ? '生成中…' : '🧩 JSON をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleParticipantsPreview('csv')}
            disabled={!canExport || participantsExportState.status === 'pending'}
          >
            👁 CSV の中身を確認
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleParticipantsPreview('json')}
            disabled={!canExport || participantsExportState.status === 'pending'}
          >
            👁 JSON の中身を確認
          </Button>
        </div>
        {participantsPreview && (
          <ExportPreviewPanel
            content={participantsPreview.content}
            format={participantsPreview.format === 'csv' ? 'csv' : 'json'}
            totalRowCount={participantsPreview.rowCount}
            title={`参加者 ${participantsPreview.format.toUpperCase()} のプレビュー`}
            onDownload={() => void handleParticipantsExport(participantsPreview.format)}
            onClose={() => setParticipantsPreview(null)}
          />
        )}
        {participantsExportState.status === 'error' && (
          <p className="mt-3 text-sm text-danger-700">
            エクスポート失敗: {participantsExportState.error.message}{' '}
            <button
              type="button"
              onClick={resetParticipantsExport}
              className="text-xs text-primary-600 hover:underline ml-2"
            >
              閉じる
            </button>
          </p>
        )}
        {participantsExportState.status === 'success' && (
          <p className="mt-3 text-xs text-slate-600">
            出力しました: {participantsExportState.result.rowCount} 名 (
            <code className="text-[10px]">{participantsExportState.result.filename}</code>)
          </p>
        )}
      </Card>

      {/* 机上調査 (ストリートビュー / 360度写真) の結果の出力 (2026-09-18 追加、docs/98 R-99)。
          机上調査点は非公開のため投稿のエクスポートに出ない。画像は「アドレス」で出す。 */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">🛰 机上調査の結果</h2>
        <p className="text-xs text-slate-500 mb-3">
          机上調査 (ストリートビュー / 360度写真) の地点と、AI
          が見つけた要素を出力します。投稿のエクスポートには
          <strong>机上調査の地点は含まれません</strong>（参加者に非公開のため）。
          列の名前・分類の名前・評価の言葉は、この地区の解析の設定・机上調査テンプレートに従います。
        </p>
        <p className="text-xs text-slate-500 mb-3">
          <strong>画像はアドレス（リンク）で出します</strong>。ストリートビューの地点は
          <strong>同じ場所・同じ向きのパノラマを開くリンク</strong>（Google
          マップ。画像そのものは保存していません）、360度写真の地点は
          <strong>写真そのもののアドレス</strong>
          です。360度写真のアドレスは、リンクを知っていれば誰でも開けます。配布先にご注意ください。
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            調査:
            <select
              value={surveyKey}
              onChange={(e) => {
                setSurveyKey(e.target.value);
                setSurveyPreview(null);
              }}
              disabled={!canExport || streetSurveyState.status === 'pending'}
              className="px-2 py-1 border border-slate-300 rounded text-sm"
              style={{ maxWidth: '100%' }}
            >
              {surveyOptions.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <span className="text-sm text-slate-700">CSV の行:</span>
          {(
            [
              ['points', '1行 = 1地点'],
              ['detections', '1行 = 1検出要素'],
            ] as [StreetSurveyExportRowMode, string][]
          ).map(([mode, label]) => (
            <label key={mode} className="flex items-center gap-1 text-sm text-slate-700">
              <input
                type="radio"
                name="street-survey-row-mode"
                checked={surveyRowMode === mode}
                onChange={() => {
                  setSurveyRowMode(mode);
                  setSurveyPreview(null);
                }}
                disabled={!canExport || streetSurveyState.status === 'pending'}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => void handleStreetSurvey('csv', false)}
            disabled={!canExport || streetSurveyState.status === 'pending'}
          >
            {streetSurveyState.status === 'pending' ? '生成中…' : '📄 CSV をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleStreetSurvey('geojson', false)}
            disabled={!canExport || streetSurveyState.status === 'pending'}
          >
            {streetSurveyState.status === 'pending' ? '生成中…' : '🗺 GeoJSON をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleStreetSurvey('csv', true)}
            disabled={!canExport || streetSurveyState.status === 'pending'}
          >
            👁 CSV の中身を確認
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleStreetSurvey('geojson', true)}
            disabled={!canExport || streetSurveyState.status === 'pending'}
          >
            👁 GeoJSON の中身を確認
          </Button>
        </div>
        {!streetSurveyEnabled && (
          <p className="mt-2 text-[11px] text-slate-400">
            ※ この地区では机上調査を使っていません。地点があれば出力できます。
          </p>
        )}
        {streetSurveyState.status === 'error' && (
          <p className="mt-3 text-sm text-danger-700">
            エクスポート失敗: {streetSurveyState.error.message}{' '}
            <button
              type="button"
              onClick={resetStreetSurvey}
              className="text-xs text-primary-600 hover:underline ml-2"
            >
              閉じる
            </button>
          </p>
        )}
        {surveyPreview && (
          <ExportPreviewPanel
            content={surveyPreview.content}
            format={surveyPreview.format === 'csv' ? 'csv' : 'geojson'}
            totalRowCount={surveyPreview.rowCount}
            title={`机上調査（${surveyPreview.surveyLabel}）${surveyPreview.format.toUpperCase()} のプレビュー — 地点 ${surveyPreview.pointCount} / 検出 ${surveyPreview.detectionCount}`}
            onDownload={() => void handleStreetSurvey(surveyPreview.format, false)}
            onClose={() => setSurveyPreview(null)}
          />
        )}
      </Card>

      {/* 分析ツールの「気づき」「処方箋」の出力 (2026-09-18 追加)。
          これまでモデレーション画面での目視以外に取り出す手段が無かった。 */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">💡 気づき・処方箋</h2>
        <p className="text-xs text-slate-500 mb-3">
          分析ツールで作られた<strong>気づきカード・処方箋カード</strong>
          を一括出力します。カードの本文 (症状・診断・処方・効能) に加えて、
          <strong>AI との対話の結果</strong> (AI の分析・AI
          からの問い)、感情・分類、もとにした投稿の ID
          も含みます。管理者が非表示にしたカードも「公開状態」列付きで出力します。CSV
          は読みやすい日本語の列、JSON は構造のまま出力します。出力操作は <code>operationLogs</code>{' '}
          に記録されます。
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-slate-700">出力するカード:</span>
          {(['insights', 'ideas'] as EcwCardExportTarget[]).map((t) => (
            <label key={t} className="flex items-center gap-1 text-sm text-slate-700">
              <input
                type="radio"
                name="ecw-card-target"
                checked={ecwTarget === t}
                onChange={() => {
                  setEcwTarget(t);
                  setEcwPreview(null);
                }}
                disabled={!canExport || ecwCardsState.status === 'pending'}
              />
              {t === 'insights' ? '気づき' : '処方箋'}
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => void handleEcwCards('csv', false)}
            disabled={!canExport || ecwCardsState.status === 'pending'}
          >
            {ecwCardsState.status === 'pending' ? '生成中…' : '📄 CSV をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleEcwCards('json', false)}
            disabled={!canExport || ecwCardsState.status === 'pending'}
          >
            {ecwCardsState.status === 'pending' ? '生成中…' : '🧩 JSON をダウンロード'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleEcwCards('csv', true)}
            disabled={!canExport || ecwCardsState.status === 'pending'}
          >
            👁 CSV の中身を確認
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleEcwCards('json', true)}
            disabled={!canExport || ecwCardsState.status === 'pending'}
          >
            👁 JSON の中身を確認
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          ※ 「個人情報を含める」が OFF
          のときは、作成者のユーザーID・ニックネーム・本名を出力しません (カードの本文と AI
          の結果は出力します)。処方箋のキャッチ画像は、URL の場合のみ出力します。
        </p>
        {ecwCardsState.status === 'error' && (
          <p className="mt-3 text-sm text-danger-700">
            エクスポート失敗: {ecwCardsState.error.message}{' '}
            <button
              type="button"
              onClick={resetEcwCards}
              className="text-xs text-primary-600 hover:underline ml-2"
            >
              閉じる
            </button>
          </p>
        )}
        {ecwPreview && (
          <ExportPreviewPanel
            content={ecwPreview.content}
            format={ecwPreview.format === 'csv' ? 'csv' : 'json'}
            totalRowCount={ecwPreview.rowCount}
            title={`${ecwPreview.target === 'insights' ? '気づき' : '処方箋'} ${ecwPreview.format.toUpperCase()} のプレビュー`}
            onDownload={() => void handleEcwCards(ecwPreview.format, false)}
            onClose={() => setEcwPreview(null)}
          />
        )}
      </Card>

      {/* Sprint BB Part 2 — Drive backup (Cloud Storage + signed URL list 返却、master 限定)。
          Drive API 直接 upload は次 sprint で追加 (現状 driveFolderUrl=null)。 */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">☁ Drive バックアップ</h2>
        <p className="text-xs text-slate-500 mb-3">
          投稿の CSV + GeoJSON を Cloud Storage の{' '}
          <code className="text-[10px]">
            backups/{`{districtId}`}/{`{timestamp}`}/
          </code>{' '}
          に書込、 5 分有効の signed URL を返します。Google Drive 直接 upload は次 sprint 予定。
          master 権限のみ実行可。
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => void handleDriveBackup('posts')}
            disabled={!canMasterOnly || driveBackupState.status === 'pending'}
          >
            {driveBackupState.status === 'pending' ? 'バックアップ中…' : '☁ posts をバックアップ'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleDriveBackup('all')}
            disabled={!canMasterOnly || driveBackupState.status === 'pending'}
          >
            {driveBackupState.status === 'pending'
              ? 'バックアップ中…'
              : '☁ 全データ (minimum=posts)'}
          </Button>
        </div>
        {driveBackupState.status === 'error' && (
          <p className="mt-3 text-sm text-danger-700">
            バックアップ失敗: {driveBackupState.error.message}{' '}
            <button
              type="button"
              onClick={resetDriveBackup}
              className="text-xs text-primary-600 hover:underline ml-2"
            >
              閉じる
            </button>
          </p>
        )}
        {driveBackupState.status === 'success' && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            <p className="text-xs text-slate-600 mb-2">
              生成完了: {driveBackupState.result.files.length} file (
              {driveBackupState.result.rowCount} 件 / 全 {driveBackupState.result.totalCount}{' '}
              件中、有効期限 {driveBackupState.result.expiresAt})
            </p>
            <ul className="space-y-1 text-xs">
              {driveBackupState.result.files.map((f) => (
                <li key={f.storagePath} className="flex items-center gap-2">
                  <a
                    href={f.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    📥 {f.filename}
                  </a>
                  <span className="text-slate-400">({(f.sizeBytes / 1024).toFixed(1)} KB)</span>
                </li>
              ))}
            </ul>
            {driveBackupState.result.driveFolderUrl === null && (
              <p className="mt-2 text-[10px] text-slate-400">
                ※ Google Drive 直接 upload は未実装 (Sprint BB+1 予定)、現状は Cloud Storage の
                signed URL のみ。
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Sprint BB Part 3 — 写真一括 download (signed URL list、master 限定、REQ-4.3.14 写真 ZIP)。
          Sprint BB Part 3.5 で fflate (~10KB, zero-dep) による client 側 ZIP bundling を実装。
          server 側 ZIP 生成は行わず client 側で並列 fetch (concurrency=4) → fflate.zip → Blob download。 */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">📷 写真 一括 download</h2>
        <p className="text-xs text-slate-500 mb-3">
          REQ-4.3.14 — 投稿写真の一括 download。server 側 ZIP 生成は行わず、callable は
          <strong> signed URL list</strong> を返し、ブラウザ上で <code>fflate</code> により ZIP
          を組み立てます (並列 fetch concurrency=4)。signed URL は 15 分有効、rate-limit 5
          call/min。master / 担当地区管理者が実行可。ZIP には投稿との紐付け用に{' '}
          <code>manifest.csv</code> / <code>posts.csv</code> を同梱します。
        </p>
        <label className="flex items-start gap-2 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={photoThumbnailOnly}
            onChange={(e) => setPhotoThumbnailOnly(e.target.checked)}
            disabled={!canExport || photoExportState.status === 'pending'}
            className="mt-1"
          />
          <span className="text-sm text-slate-700">
            <strong>thumbnail のみ</strong> (チェックを外すと original + thumbnail 両方)
          </span>
        </label>
        {canExportUnblurred && (
          <label className="flex items-start gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={photoUnblurred}
              onChange={(e) => setPhotoUnblurred(e.target.checked)}
              disabled={photoThumbnailOnly || photoExportState.status === 'pending'}
              className="mt-1"
              data-testid="export-photo-unblurred"
            />
            <span className="text-sm text-slate-700">
              <strong>ぼかし前の写真で出力する</strong>
              （顔・ナンバーのぼかしを掛ける前の写真。ぼかし前の写真が保存されていない投稿はぼかし済みを出します。出力は操作記録に残ります）
            </span>
          </label>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            type="button"
            onClick={() => void handlePhotoExport()}
            disabled={!canExport || photoExportState.status === 'pending'}
          >
            {photoExportState.status === 'pending' ? '取得中…' : '📷 写真 URL を一括取得'}
          </Button>
          {photoExportState.status === 'success' && photoExportState.result.files.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handlePhotoZipDownload()}
              disabled={photoZipState.status === 'pending'}
            >
              {photoZipState.status === 'pending'
                ? `ZIP 作成中… (${photoZipState.done}/${photoZipState.total})`
                : '⬇ ZIP でダウンロード'}
            </Button>
          )}
        </div>
        {photoZipState.status === 'pending' && (
          <div className="mt-3">
            <progress value={photoZipState.done} max={photoZipState.total} className="w-full h-2" />
            <p className="text-xs text-slate-600 mt-1">
              {photoZipState.done}/{photoZipState.total} (
              {Math.round((photoZipState.done / photoZipState.total) * 100)}%)
              {photoZipState.failed > 0 && (
                <span className="text-danger-600 ml-2">失敗 {photoZipState.failed}</span>
              )}
            </p>
          </div>
        )}
        {photoZipState.status === 'success' && (
          <p className="mt-3 text-xs text-slate-600">
            ZIP 作成完了: {photoZipState.total - photoZipState.failed}/{photoZipState.total} file
            {photoZipState.failed > 0 && (
              <span className="text-danger-600 ml-2">
                ({photoZipState.failed} file は取得失敗のため skip)
              </span>
            )}
          </p>
        )}
        {photoZipState.status === 'error' && (
          <p className="mt-3 text-sm text-danger-700">ZIP 作成失敗: {photoZipState.message}</p>
        )}
        {photoExportState.status === 'error' && (
          <p className="mt-3 text-sm text-danger-700">
            取得失敗: {photoExportState.error.message}{' '}
            <button
              type="button"
              onClick={resetPhotoExport}
              className="text-xs text-primary-600 hover:underline ml-2"
            >
              閉じる
            </button>
          </p>
        )}
        {photoExportState.status === 'success' && (
          <div className="mt-3 border-t border-slate-200 pt-3">
            <p className="text-xs text-slate-600 mb-2">
              写真 {photoExportState.result.files.length} file /{' '}
              {photoExportState.result.photoCount} post (全 {photoExportState.result.postCount}{' '}
              件中)、有効期限 {photoExportState.result.expiresAt}
            </p>
            {photoExportState.result.files.length === 0 ? (
              <p className="text-xs text-slate-500">写真付き post がありませんでした。</p>
            ) : (
              <ul className="space-y-1 text-xs max-h-64 overflow-y-auto">
                {photoExportState.result.files.map((f) => (
                  <li key={`${f.postId}_${f.kind}`} className="flex items-center gap-2">
                    <a
                      href={f.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      📥 {f.filename}
                    </a>
                    <span className="text-slate-400">({f.kind})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">🪪 カード印刷</h2>
        <p className="text-xs text-slate-500 mb-3">
          A-S6b / GAP-A8 — 投稿を A4 名刺サイズ (55×91mm) 10 面付けで印刷します。 PDF lib
          は使わずブラウザ標準の印刷ダイアログから出力します。
        </p>
        {districtId && canExport ? (
          <Link to={`/districts/${districtId}/export/cards`}>
            <Button type="button" variant="secondary">
              🪪 カード印刷を開く
            </Button>
          </Link>
        ) : (
          <Button type="button" variant="secondary" disabled>
            🪪 カード印刷を開く
          </Button>
        )}
      </Card>

      {exportState.status === 'success' && (
        <Card padding="md">
          <h2 className="text-sm font-bold text-slate-900 mb-2">直近の出力</h2>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">filename</dt>
            <dd className="text-slate-800">
              <code className="text-[10px]">{exportState.result.filename}</code>
            </dd>
            <dt className="text-slate-500">format</dt>
            <dd className="text-slate-800">
              <code>{exportState.result.format}</code>
            </dd>
            <dt className="text-slate-500">rowCount</dt>
            <dd className="text-slate-800">
              {exportState.result.rowCount} 件 (全 {exportState.result.totalCount} 件)
            </dd>
            <dt className="text-slate-500">includePersonal</dt>
            <dd className="text-slate-800">
              <code>{String(exportState.result.includePersonal)}</code>
            </dd>
            <dt className="text-slate-500">exportedAt</dt>
            <dd className="text-slate-800">{exportState.result.exportedAt}</dd>
          </dl>
        </Card>
      )}
    </>
  );
}
